import { eventTemplate } from "../../js/state-machine/events";
import { prefix } from "../../js/utilities";
import {
    getFirstDayOfMonth,
    getTotalDaysInMonth,
    isDateAfter,
    isDateBefore,
    isSameDateOrAfter,
    isSameDateOrBefore,
} from "../../js/utils/date";
import { matchers } from "../../js/validate-props";

export const componentName = "Calendar";

export const defaultProps = Object.freeze({
    cancelLabel: "Cancel",
    confirmLabel: "Confirm",
    endDate: new Date(2100, 11, 31),
    format: "MM/DD/YYYY",
    inactiveDates: [],
    locale: "en-US",
    monthSrLabel: "Month",
    nextSrLabel: "Next month",
    prevSrLabel: "Previous month",
    onCancelClick: () => {},
    onConfirmClick: () => {},
    onSelectDate: () => {},
    startDate: new Date(1900, 0, 1),
    selectedDate: undefined,
    todayLabel: "Today",
    weekStart: "sun",
    yearSrLabel: "Year",
});

export const schema = Object.freeze({
    cancelLabel: matchers.string,
    confirmLabel: matchers.string,
    endDate: matchers.or(matchers.undefined, matchers.date),
    format: matchers.string,
    inactiveDates: (value) => {
        return (
            matchers.array(value) &&
            !value.some((date) => {
                return !matchers.date(date);
            })
        );
    },
    locale: matchers.string,
    monthSrLabel: matchers.string,
    nextSrLabel: matchers.string,
    prevSrLabel: matchers.string,
    onCancelClick: matchers.function,
    onConfirmClick: matchers.function,
    onSelectDate: matchers.function,
    startDate: matchers.or(matchers.undefined, matchers.date),
    selectedDate: matchers.or(matchers.undefined, matchers.date),
    todayLabel: matchers.string,
    weekStart: matchers.or(matchers.eq("sun"), matchers.eq("mon"), matchers.eq("sat")),
    yearSrLabel: matchers.string,
});

export const eventMap = Object.freeze({
    selectDate: eventTemplate(componentName, "SelectDate"),
});

export const componentClasses = Object.freeze({
    calendar: {
        base: `${prefix}calendar`,
        isPressing: `${prefix}calendar--is-pressing`,
    },
    header: `${prefix}calendar__header`,
    headerDisplay: `${prefix}calendar__header-display`,
    headerPolite: `${prefix}calendar__header-polite`,
    monthSelect: `${prefix}calendar__month-select`,
    yearSelect: `${prefix}calendar__year-select`,
    prevButton: `${prefix}calendar__prev-button`,
    nextButton: `${prefix}calendar__next-button`,
    body: `${prefix}calendar__body`,
    grid: {
        head: `${prefix}calendar__grid-head`,
        body: `${prefix}calendar__grid-body`,
        row: `${prefix}calendar__grid-row`,
    },
    weekDay: `${prefix}calendar__week-day`,
    day: {
        base: `${prefix}calendar__day`,
        inactive: `${prefix}calendar__day--inactive`,
        outMonth: `${prefix}calendar__day--out-month`,
        today: `${prefix}calendar__day--today`,
        selected: `${prefix}calendar__day--selected`,
        focusable: `${prefix}calendar__day--focusable`,
    },
    footer: `${prefix}calendar__footer`,
    today: `${prefix}calendar__today`,
    cancel: `${prefix}calendar__cancel`,
    confirm: `${prefix}calendar__confirm`,
});

export const views = Object.freeze({
    calendar: {
        base: {
            classList: [`!${componentClasses.calendar.isPressing}`],
        },
        isPressing: {
            classList: [componentClasses.calendar.isPressing],
        },
    },
    day: {
        base: {
            classList: [
                componentClasses.day.base,
                `!${componentClasses.day.outMonth}`,
                `!${componentClasses.day.today}`,
                `!${componentClasses.day.selected}`,
                `!${componentClasses.day.inactive}`,
            ],
        },
        focusable: {
            classList: [componentClasses.day.focusable],
            attrs: { tabindex: "0" },
        },
        notFocusable: {
            classList: [`!${componentClasses.day.focusable}`],
            attrs: { tabindex: "-1" },
        },
    },
});

// Number of slots on calendar
export const slotNumber = 42;

// Daynames based on index from Date object
export const dayNames = {
    sun: 0,
    mon: 1,
    tue: 2,
    wed: 3,
    thu: 4,
    fri: 5,
    sat: 6,
};

/**
 * Verify if a date meet all the requirements to be valid
 * @param {Date} date
 * @param {Date} startDate
 * @param {Date} endDate
 * @param {Array<Date>} inactiveDates
 * @returns {Boolean}
 */
export const isValidDate = (date, startDate, endDate, inactiveDates) => {
    return (
        matchers.date(date) &&
        isSameDateOrAfter(date, startDate) &&
        isSameDateOrBefore(date, endDate) &&
        !getInactiveDatesMap(inactiveDates).has(getDateHash(date))
    );
};

/**
 * Get week day index based on dayNames and javascript Date
 * @param {String} weekStartDay - Week day name based on dayNames keys (From sun[0] to sat[6]).
 * @returns {Number} - From 0 (sun) to 6 (sat)
 */
export const getWeekStartDayIndex = (weekStartDay) => {
    return Object.keys(dayNames).findIndex((dayName) => {
        return dayName === weekStartDay;
    });
};

/**
 * NOTE: Calendar Calendar is based on a 42 slots grid
 * Get the slot index of the first day of month based on the day that week starts
 * @param {Date} date - only month matters
 * @param {Number} weekStartIndex - Index of the starting day of the week (dayNames)
 * @returns {Number} - Slot index
 */
export const getFirstDayOfMonthSlotIndex = (date, weekStartIndex) => {
    const firstDayOfMonth = new Date(getFirstDayOfMonth(date));
    const firstDayOfMonthWeekDayIndex = firstDayOfMonth.getDay();

    if (weekStartIndex === firstDayOfMonthWeekDayIndex) {
        return 0;
    }
    if (weekStartIndex < firstDayOfMonthWeekDayIndex) {
        return firstDayOfMonthWeekDayIndex - weekStartIndex;
    }
    if (weekStartIndex > firstDayOfMonthWeekDayIndex) {
        return Math.abs(weekStartIndex - firstDayOfMonthWeekDayIndex - 7);
    }
};

/**
 * NOTE: Calendar Calendar is based on a 42 slots grid
 * Get the slot index of the last day of month based on the day that week starts
 * @param {Date} date - only month matters
 * @param {Number} weekStartIndex - Index of the starting day of the week (dayNames)
 * @returns {Number} - Slot index
 */
export const getLastDayOfMonthSlotIndex = (date, weekStartIndex) => {
    const firstDayOfMonthSlotIndex = getFirstDayOfMonthSlotIndex(date, weekStartIndex);

    return firstDayOfMonthSlotIndex + (getTotalDaysInMonth(date) - 1);
};

/**
 * NOTE: Calendar Calendar is based on a 42 slots grid
 * Get the first slot date based on the day that week starts
 * @param {Date} date - only month matters
 * @param {Number} weekStartIndex - Index of the starting day of the week (dayNames)
 * @returns {Date}
 */
export const getFirstSlotDate = (date, weekStartIndex) => {
    const firstSlotDate = new Date(getFirstDayOfMonth(date));
    firstSlotDate.setDate(
        firstSlotDate.getDate() - getFirstDayOfMonthSlotIndex(date, weekStartIndex)
    );

    return firstSlotDate;
};

/**
 * NOTE: Calendar Calendar is based on a 42 slots grid
 * Get the last slot date based on the day that week starts
 * @param {Date} date - only month matters
 * @param {Number} weekStartIndex - Index of the starting day of the week (dayNames)
 * @returns {Date}
 */
export const getLastSlotDate = (date, weekStartIndex) => {
    const lastSlotDate = new Date(getFirstSlotDate(date, weekStartIndex));
    lastSlotDate.setDate(lastSlotDate.getDate() + 41);

    return lastSlotDate;
};

/**
 * NOTE: Calendar Calendar is based on a 42 slots grid
 * Check if a date is outside of slots given a base date and the day that week starts to build the range of slots
 * @param {Date} date
 * @param {Date} slotsBaseDate - only month matters
 * @param {Number} weekStartIndex - Index of the starting day of the week (dayNames)
 * @returns {Boolean}
 */
export const isDateOutsideSlots = (date, slotsBaseDate, weekStartIndex) => {
    return (
        isDateBefore(date, getFirstSlotDate(slotsBaseDate, weekStartIndex)) ||
        isDateAfter(date, getLastSlotDate(slotsBaseDate, weekStartIndex))
    );
};

/**
 * Generating date hash for mapping date value
 * @param {String} date - Date object
 * @returns {String} date hash value
 */
export const getDateHash = (date) => {
    if (!matchers.date(date)) throw new Error("Invalid date");
    return `${date.getFullYear()}${date.getMonth()}${date.getDate()}`;
};

/**
 * Map inactive date for easier access to that data
 * @param {Array} inactiveDates - Array of date objects
 * @returns Inactive dates map
 */
export const getInactiveDatesMap = (inactiveDates) => {
    const inactiveDatesMap = new Map();

    inactiveDates.forEach((date) => {
        inactiveDatesMap.set(getDateHash(date), date);
    });

    return inactiveDatesMap;
};
